import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { camelToSnakeCase, snakeToCamelCase } from 'utils/api';
import QueryString from 'qs';

import StandaloneSnackbar from 'components/StandaloneSnackbar/StandaloneSnackbar';
import { getAccessToken } from 'utils/keycloak-adapter';

export interface IPaginationParams {
  page: number;
  limit: number;
}

export interface IPaginationResponse<T> extends IPaginationParams {
  items: T[];
  total: number;
}

const baseURL = process.env.REACT_APP_INSIGHTS_API_URL;

const axiosInstance = () =>
  axios.create({
    baseURL,
  });

const api = axiosInstance();

api.interceptors.request.use(async (config: AxiosRequestConfig) => {
  const newConfig = { ...config };

  if (!config.params?.isPublicRequest) {
    const token = getAccessToken();

    newConfig.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  } else {
    delete newConfig.params.isPublicRequest;
  }

  if (config.params) {
    newConfig.params = camelToSnakeCase(config.params);
  }

  if (config.data && !(config.data instanceof FormData)) {
    newConfig.data = camelToSnakeCase(config.data);
  }

  return newConfig;
});

api.interceptors.response.use((res: AxiosResponse) => {
  if (res.data && res.headers['content-type'] === 'application/json') {
    res.data = snakeToCamelCase(res.data);
  }
  return res;
});

api.interceptors.request.use((req: AxiosRequestConfig) => {
  const paramsSerializer = (params) => {
    return QueryString.stringify(params).replaceAll(/%5B\d%5D/g, '');
  };

  return { ...req, paramsSerializer };
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error) {
      return Promise.reject();
    }

    if (error.response?.status === 401 && window) {
      window.location.href = '/logout';
    }

    if (error.response?.status === 403) {
      const url: string = error.config?.url || '';
      const isExternalApprovalEndpoint = url.includes('/external-approval');
      if (!isExternalApprovalEndpoint) {
        StandaloneSnackbar.error('Operação cancelada por falta de autorização.');
      }
    }

    return Promise.reject(error.response);
  }
);

export default api;
